import Contact from '@repo/utils/models/Contact';
import Collection from '@repo/utils/models/Collection';
import Occasion from '@repo/utils/helpers/Occasion';
import type { ID, ReduxAction } from '@repo/utils/types';
import Note from '@repo/utils/models/Note';
import { Map } from 'immutable';
import { searchResponseToContactTuples } from './search/createSearchReducer';

export interface ContactsState {
  collection: Collection<Contact>;
  mergingContactBatch: boolean;
}

const initialContactsState = {
  collection: new Collection<Contact>(),
  mergingContactBatch: false,
};

const addContactsToCollection = (
  state: ContactsState,
  incomingContacts: Map<ID, Contact>,
  deprioritize?: boolean,
) => ({
  ...state,
  collection: state.collection.copyWith({
    items: state.collection.items.mergeWith((existingRecord, newRecord) => {
      if (existingRecord && deprioritize) {
        return existingRecord;
      } else {
        if (newRecord.isMoreRecentlyUpdatedThan(existingRecord)) {
          return newRecord;
        } else {
          const _snippetResult = newRecord._snippetResult;
          return newRecord.copyWith({
            ...existingRecord,
            ...(_snippetResult ? { _snippetResult } : {}),
          });
        }
      }
    }, incomingContacts),
  }),
});

const contactToTuple = (contact) => [String(contact.id), new Contact(contact)];

export function contactsReducer(
  state: ContactsState = initialContactsState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'FETCH_CONTACT_REQUESTED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(String(payload.id), (contact) =>
            contact
              ? contact.copyWith({ isFetching: true, error: false })
              : new Contact({ id: payload.id, isFetching: true }),
          ),
        }),
      };

    case 'FETCH_CONTACT_SUCCEEDED':
      if (!payload.contact) {
        return state;
      }

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            String(payload.contact.objectID),
            new Contact({ ...payload.contact, updated: new Occasion() }),
            (contact) =>
              contact.copyWith({
                ...payload.contact,
                isFetching: false,
                updated: new Occasion(),
              }),
          ),
        }),
      };

    case 'FETCH_CONTACT_TIMELINE_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(String(payload.contactID), (contact) =>
            contact?.copyWith({ notesLoaded: true }),
          ),
        }),
      };

    // We only fetch contact data from Typesense once per session, and only update our
    // store if we haven't yet loaded the contact from the Clay API
    case 'FETCH_ELASTICSEARCH_CONTACT_SUCCEEDED':
      const elasticSearchContact = new Contact(payload.response);
      const contactID = elasticSearchContact.id;
      const cachedContact = state.collection.items.get(contactID);

      if (elasticSearchContact.isMoreRecentlyUpdatedThan(cachedContact)) {
        return {
          ...state,
          collection: state.collection.copyWith({
            items: state.collection.items.update(contactID, (contact) =>
              contact ? contact.copyWith(elasticSearchContact) : elasticSearchContact,
            ),
          }),
        };
      } else {
        return state;
      }

    case 'FETCH_CONTACT_FAILED':
      const storedContact = state.collection.items.get(String(payload.contactID));
      const shouldError = payload.status === 404 || !storedContact?.isLoaded;

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(String(payload.contactID), (contact) =>
            contact.copyWith({ error: shouldError, isFetching: false }),
          ),
        }),
      };

    case 'UPDATE_CONTACT_SUCCEEDED':
    case 'UPDATE_CONTACT_FORM_SUCCEEDED':
      // merge in the new contact info
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            String(payload.id || payload.objectID),
            new Contact(),
            (contact) => contact.copyWith({ ...payload, updated: new Occasion() }),
          ),
        }),
      };

    case 'UPDATE_CONTACT_PROFILE_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            String(payload.contactID),
            new Contact(),
            (contact) => contact.copyWith(payload.profile),
          ),
        }),
      };

    case 'MERGE_CONTACT_BATCH_REQUESTED':
      return {
        ...state,
        mergingContactBatch: true,
      };

    case 'MERGE_CONTACT_BATCH_SUCCEEDED':
      const contactPayload = payload?.response?.contact || payload?.contact;

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            contactPayload?.objectID,
            new Contact(),
            (contact) => {
              if (contact) {
                return contact.copyWith({
                  ...contactPayload,
                  updated: new Occasion(),
                });
              }
            },
          ),
        }),
        mergingContactBatch: false,
      };

    case 'MERGE_CONTACT_BATCH_FAILED':
      return {
        ...state,
        mergingContactBatch: false,
      };

    // dump search results into our cache of contacts
    case 'FETCH_SEARCH_RESULTS_SUCCEEDED':
    case 'FETCH_AD_HOC_SEARCH_RESULTS_SUCCEEDED':
    case 'FETCH_MENTION_SEARCH_RESULTS_SUCCEEDED':
      return addContactsToCollection(
        state,
        Map(searchResponseToContactTuples(payload.response, true)),
      );

    case 'FETCH__ALL_PEOPLE_SEARCH_RESULTS_SUCCEEDED':
    case 'FETCH_GROUP_SUCCEEDED':
      return addContactsToCollection(
        state,
        Map(searchResponseToContactTuples(payload.response, false)),
      );

    case 'FETCH_CONTACTS_AT_ORGANIZATION_SUCCEEDED':
      return addContactsToCollection(state, Map(payload.response.map(contactToTuple)));

    // dump activity contacts into our cache of contacts
    case 'FETCH_ALL_ACTIVITY_SUCCEEDED':
      const activityContacts = payload.response.results.flatMap((activityItem) => {
        if (activityItem?.metadata?.contact) {
          return [contactToTuple(activityItem.metadata.contact)];
        } else if (activityItem?.metadata?.contacts) {
          return activityItem.metadata.contacts.map(contactToTuple);
        }
        return [];
      });

      return addContactsToCollection(state, Map(activityContacts), true);

    // dump any notes contacts into cache
    case 'FETCH_ALL_NOTES_SUCCEEDED':
      const notesContacts = payload.response.results.reduce(
        (contactMap: Map<ID, Contact>, note) => {
          const contact = note.contact;
          if (!contact || contactMap.has(String(contact.id))) {
            return contactMap;
          }
          return contactMap.set(String(contact.id), new Contact(contact));
        },
        Map(),
      );

      return addContactsToCollection(state, notesContacts, true);

    case 'FETCH_ALL_DUPLICATES_SUCCEEDED':
      const duplicatesContacts = payload?.response?.results.reduce(
        (contactMap: Map<ID, Contact>, duplicateItem) => {
          const contacts = duplicateItem.contacts;

          if (!contacts || contacts.length === 0) {
            return contactMap;
          }

          return contacts.reduce(
            (contactMap, contact) => contactMap.set(String(contact.id), new Contact(contact)),
            contactMap,
          );
        },
        Map(),
      );

      return addContactsToCollection(state, duplicatesContacts, true);

    case 'CREATE_NOTE_SUCCEEDED':
      const note = new Note({
        ...payload.response,
        contact: { id: payload.response.contact },
      });
      const contactForNote = state.collection.items.get(note.contact.id);

      if (contactForNote && note.mentions) {
        const newRelateds = note.mentions
          .map((id) => ({ id }))
          .concat(contactForNote.related)
          .toArray();

        return {
          ...state,
          collection: state.collection.copyWith({
            items: state.collection.items.update(String(note.contact.id), (contact) =>
              contact.copyWith({ related: newRelateds }),
            ),
          }),
        };
      }

      return state;

    case 'CREATE_CONTACT_INVITE_REQUESTED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(payload.contact.id, (contact) =>
            contact.copyWith({ invited: true }),
          ),
        }),
      };

    case 'CREATE_CONTACT_INVITE_FAILED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(payload.contact.id, (contact) =>
            contact.copyWith({ invited: false }),
          ),
        }),
      };

    // We have cases where we have contact data in the UI that can be used to "select" a contact, but that data
    // is not yet in the store, and selecting the contact does not otherwise lead to a fetch for fresh data. We
    // can seed the store with this data if the contact isn't already cached.
    case 'SELECT_CONTACT':
    case 'MAYBE_CACHE_CONTACT':
      if (!payload.contact || state.collection.items.has(payload.contact?.id)) {
        return state;
      }

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            payload.contact.id,
            (storedContact) => storedContact || payload.contact,
          ),
        }),
      };

    case 'CACHE_CONTACTS':
      return addContactsToCollection(
        state,
        Map(payload.contacts.map((contact) => [contact.id, contact])),
        true,
      );

    case 'CREATE_CONTACT_SUCCEEDED':
      if (payload.response?.id) {
        return {
          ...state,
          collection: state.collection.copyWith({
            items: state.collection.items.set(
              String(payload.response.id),
              new Contact(payload.response),
            ),
          }),
        };
      } else {
        return state;
      }

    case 'DELETE_CONTACT_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.delete(String(payload.id)),
        }),
      };

    case 'DELETE_CONTACT_BATCH_REQUESTED':
      const deletedContactIDs = payload.contact_ids.map(String);
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.filter(
            (_, contactID) => !deletedContactIDs.includes(contactID),
          ),
        }),
      };

    case 'FETCH_EXISTING_CONTACT_FOR_LINKEDIN_SLUG_SUCCEEDED':
    case 'CREATE_CONTACT_FROM_LINKEDIN_PROFILE_SUCCEEDED':
    case 'MERGE_CONTACT_FROM_LINKEDIN_PROFILE_SUCCEEDED':
      if (!payload.contact) {
        return state;
      }

      const newContact = new Contact(payload.contact);

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.set(String(newContact.id), newContact),
        }),
      };

    // Until we are keeping contacts in the store, these action types don't have a ton of utility. However, we can
    // leverage them for app alerts. Keeping them here as a reference.
    case 'CREATE_CONTACT':
    case 'CREATE_CONTACT_REQUESTED':
    case 'CREATE_CONTACT_FAILED':
    default:
      return state;
  }
}
